import LocalizedStrings from "react-localization";
import { useContext, useEffect, useState } from "react";
import {
  Button,
  Flex,
  Grid,
  Label,
  Loader,
  TextAreaField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import toast from "react-hot-toast";
import html2pdf from "html2pdf.js";
import { getDistance } from "geolib";
import { Pause, PlayArrow, Save, Stop } from "@mui/icons-material";

import ServiceOrderInfo from "components/serviceOrders/ServiceOrderInfo";
import ToolsInfo from "components/tools/ToolsInfo";
import SparesInfo from "components/spares/SparesInfo";
import ExecutorCard from "components/serviceOrders/ExecutorCard";
import MaintenanceRoutinesInfo from "components/maintenanceRoutines/MaintenanceRoutinesInfo";
import { OrderContext } from "components/serviceOrders/context/OrderContext";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import {
  createExecutedServiceOrder,
  createServiceOrderLog,
  updateExecutedServiceOrder,
  updateServiceOrder,
  updateSpare,
} from "graphql/mutations";
import { LayoutContext } from "layout/context/LayoutContext";
import ExecutedOrderCard from "components/serviceOrders/ExecutedOrderCard";
import {
  executedServiceOrdersByServiceOrderID,
  getActivity,
  getEquipment,
  getUser,
  serviceOrderPhotosByServiceOrderID,
} from "graphql/queries";
import { serviceOrderStatus } from "config/constants";
import { getCompanyName } from "graphql/custom-queries";
import { useOnlineStatus } from "hooks/useOnlineStatus";
import ServiceOrderLogs from "../ServiceOrderLogs";
import EquipmentsList from "components/equipments/EquipmentsList";

const centerMap = {
  lat: 6.25184,
  lng: -75.56359,
};

const ExecutedServiceOrder = ({ onSuccess }) => {
  const {
    serviceOrder,
    routines,
    onChangeObservations,
    viewType,
    fromEquipment,
    localData,
    saveLocalData,
    cleanLocalData,
    saveLocalUsers,
    saveLocalTools,
    saveLocalSpares,
    saveLocalEquipments,
    saveLocalRoutines,
    saveLocalExecutedService,
  } = useContext(OrderContext);
  const online = useOnlineStatus();
  const { me } = useContext(LayoutContext);
  const [loading, setLoading] = useState(false);
  const [executorCompany, setExecutorCompany] = useState({
    data: null,
    loading: false,
  });

  const onResume = async () => {
    try {
      setLoading(true);

      if (online) await updateStatus(serviceOrderStatus.executing);

      await saveExecution({
        serviceOrderID: serviceOrder.id,
        userExecutor: me.id,
        executedRoutines: routines ?? [],
      });

      toast.success(strings.successResumed);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  const onPause = async () => {
    try {
      setLoading(true);

      if (online) await updateStatus(serviceOrderStatus.paused);

      await saveExecution({
        serviceOrderID: serviceOrder.id,
        userExecutor: me.id,
        executedRoutines: routines ?? [],
      });

      toast.success(strings.successPaused);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  const onSave = async () => {
    try {
      setLoading(true);

      await saveExecution({
        serviceOrderID: serviceOrder.id,
        userExecutor: me.id,
        executedRoutines: await mapRoutinesWithActivities(routines ?? []),
      });

      toast.success(strings.successSaved);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  const onExport = async () => {
    try {
      setLoading(true);

      const el = document.querySelector(
        `#export-pdf-order-${serviceOrder.code}`,
      );
      const opt = {
        margin: 0.2,
        filename: `order-${serviceOrder.code}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 1.5, useCORS: true, scrollY: 0 },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
        pagebreak: {
          mode: ["css", "legacy"],
          avoid: ".avoidBreak",
        },
      };

      await html2pdf().set(opt).from(el).save();

      toast.success(strings.successExported);

      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  const onLocalSubmit = async () => {
    try {
      setLoading(true);

      await saveLocalData({
        serviceOrderID: serviceOrder.id,
        userExecutor: me.id,
        executedRoutines: routines ?? [],
      });
      await saveLocalUsers();
      await saveLocalTools();
      await saveLocalSpares();
      await saveLocalEquipments();
      await saveLocalRoutines();
      await saveLocalExecutedService();

      await API.graphql(
        graphqlOperation(updateServiceOrder, {
          input: { id: serviceOrder.id, offline: true },
        }),
      );

      await API.graphql({
        query: createServiceOrderLog.replaceAll("__typename", ""),
        variables: {
          input: {
            serviceOrderID: serviceOrder.id,
            status: "offline",
            userID: me.id,
            companyID: serviceOrder.companyID,
          },
        },
      });

      toast.success(strings.successSavedLocal);

      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  const onLocalClean = async () => {
    try {
      setLoading(true);

      await cleanLocalData();

      await API.graphql(
        graphqlOperation(updateServiceOrder, {
          input: { id: serviceOrder.id, offline: false },
        }),
      );

      await API.graphql({
        query: createServiceOrderLog.replaceAll("__typename", ""),
        variables: {
          input: {
            serviceOrderID: serviceOrder.id,
            status: "online",
            userID: me.id,
            companyID: serviceOrder.companyID,
          },
        },
      });

      toast.success(strings.successCleanLocal);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      if (me.isTechnician && me.id !== serviceOrder.userExecutor) {
        toast.error(strings.invalidTechnician);
        return;
      }

      if (serviceOrder.status === serviceOrderStatus.canceled) {
        toast.error(strings.orderCanceled);
        return;
      }

      if (
        serviceOrder.status !== serviceOrderStatus.executing &&
        serviceOrder.status !== serviceOrderStatus.paused
      ) {
        await startExecution();
        return;
      }

      if (!!serviceOrder.photoRequired) {
        const photos = await fetchServiceOrderPhotos(serviceOrder.id);
        if (!photos || photos?.length <= 0) {
          toast.error(strings.photosRequired);
          return;
        }
      }

      if (!!serviceOrder.geolocation && me.isTechnician) {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              const currentLatitude = pos.coords.latitude;
              const currentLongitude = pos.coords.longitude;

              const equipments =
                serviceOrder.equipments?.length > 0
                  ? await fetchEquipments(serviceOrder.equipments)
                  : [];

              const isNear =
                equipments?.some((eq) => {
                  const distance = getDistance(
                    {
                      latitude: currentLatitude,
                      longitude: currentLongitude,
                    },
                    {
                      latitude: eq.coordsLat || centerMap.lat,
                      longitude: eq.coordsLng || centerMap.lng,
                    },
                  );

                  return distance <= 300;
                }) ?? false;

              if (!isNear) {
                toast.error(strings.soFarFromService);
                return;
              } else await endExecution();
            },
            () => {
              toast.error(strings.responses.locationNotFound);
            },
          );
        } else {
          toast.error(strings.responses.locationNotSupported);
        }
      } else await endExecution();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  const saveExecution = async (values) => {
    if (localData) {
      saveLocalData({
        ...values,
        serviceOrderID: serviceOrder.id,
        userExecutor: me.id,
        executedRoutines: routines ?? [],
      });
      if (!online) return;
    }
    const executionData = await API.graphql(
      graphqlOperation(executedServiceOrdersByServiceOrderID, {
        serviceOrderID: serviceOrder.id,
      }),
    );
    const executionItem =
      executionData.data.executedServiceOrdersByServiceOrderID.items[0];

    await API.graphql({
      query: updateServiceOrder.replaceAll("__typename", ""),
      variables: {
        input: {
          id: serviceOrder.id,
          observations: serviceOrder.observations,
        },
      },
    });

    if (!executionItem) {
      await API.graphql({
        query: createExecutedServiceOrder.replaceAll("__typename", ""),
        variables: {
          input: {
            ...values,
          },
        },
      });
    } else {
      await API.graphql({
        query: updateExecutedServiceOrder.replaceAll("__typename", ""),
        variables: {
          input: {
            id: executionItem.id,
            ...values,
          },
        },
      });
    }
  };

  const updateStatus = async (status) => {
    await API.graphql({
      query: updateServiceOrder.replaceAll("__typename", ""),
      variables: {
        input: {
          id: serviceOrder.id,
          status,
        },
      },
    });
    await API.graphql({
      query: createServiceOrderLog.replaceAll("__typename", ""),
      variables: {
        input: {
          serviceOrderID: serviceOrder.id,
          status,
          userID: me.id,
          companyID: serviceOrder.companyID,
        },
      },
    });
  };

  const startExecution = async () => {
    try {
      setLoading(true);

      if (online) await updateStatus(serviceOrderStatus.executing);

      await saveExecution({
        serviceOrderID: serviceOrder.id,
        userExecutor: me.id,
        executedRoutines: await mapRoutinesWithActivities(routines ?? []),
        executionStartedAt: new Date(),
      });

      toast.success(strings.successStarted);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  const endExecution = async () => {
    await API.graphql({
      query: updateServiceOrder.replaceAll("__typename", ""),
      variables: {
        input: {
          id: serviceOrder.id,
          status: serviceOrderStatus.executed,
        },
      },
    });

    await API.graphql({
      query: createServiceOrderLog.replaceAll("__typename", ""),
      variables: {
        input: {
          serviceOrderID: serviceOrder.id,
          status: serviceOrderStatus.executed,
          userID: me.id,
          companyID: serviceOrder.companyID,
        },
      },
    });

    await saveExecution({
      userExecutor: me.id,
      executedRoutines: await mapRoutinesWithActivities(routines),
      executionEndedAt: new Date(),
    });

    if (serviceOrder.equipmentSpares?.length > 0)
      await updateSpares(
        serviceOrder.equipmentSpares.reduce(
          (list, current) => [
            ...list,
            ...(current.spares?.map((sp) => ({
              equipmentID: current.equipmentID,
              spareID: sp,
            })) ?? []),
          ],
          [],
        ),
      );

    if (localData) cleanLocalData();

    toast.success(strings.successExecuted);
    if (onSuccess) onSuccess();
  };

  async function updateSpares(values) {
    try {
      const promises = [];

      promises.push(
        ...(values?.map((val) =>
          API.graphql({
            query: updateSpare.replaceAll("__typename", ""),
            variables: {
              input: {
                id: val.spareID,
                equipmentID: val.equipmentID,
              },
            },
          }),
        ) ?? []),
      );

      await Promise.all(promises ?? []);
    } catch (err) {
      console.log("Error updateSpares", err);
    }
  }

  async function fetchEquipments(ids) {
    try {
      const equipmentItems = [];
      if (ids.length > 0) {
        await Promise.all(
          ids.map(async (id) => {
            const equipmentData = await API.graphql(
              graphqlOperation(getEquipment, { id }),
            );
            if (equipmentData.data.getEquipment) {
              equipmentItems.push(equipmentData.data.getEquipment);
            }
          }),
        );
      }
      return equipmentItems;
    } catch (err) {
      toast.error(strings.responses.errorFetchEquipments);
    }
  }

  async function fetchExecutorCompany(userId) {
    try {
      setExecutorCompany((prev) => ({ ...prev, loading: true }));
      const userData = await API.graphql(
        graphqlOperation(getUser, {
          id: userId,
        }),
      );
      const userItem = userData.data.getUser;

      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id: userItem.companyID,
        }),
      );
      const companyItem = companyData.data.getCompany;

      setExecutorCompany((prev) => ({ ...prev, data: companyItem.name }));
    } catch (err) {
      console.log("Error fetchExecutorCompany", err);
    } finally {
      setExecutorCompany((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchServiceOrderPhotos(id) {
    try {
      const serviceOrderPhotosData = await API.graphql(
        graphqlOperation(serviceOrderPhotosByServiceOrderID, {
          serviceOrderID: id,
        }),
      );
      return serviceOrderPhotosData.data.serviceOrderPhotosByServiceOrderID
        .items;
    } catch (err) {
      toast.error(strings.responses.errorFetchPhotos);
    }
  }

  async function mapRoutinesWithActivities(routinesToMap) {
    const activitiesIds = [
      ...new Set(
        routinesToMap
          ?.map((r) => r.activities)
          ?.flat()
          ?.map((r) => r.activityID) ?? [],
      ),
    ];

    let activities = [];
    if (activitiesIds?.length > 0) {
      await Promise.all(
        activitiesIds.map(async (id) => {
          const activityData = await API.graphql(
            graphqlOperation(getActivity, {
              id,
            }),
          );
          const activityItem = activityData.data.getActivity;

          activities.push(activityItem);
        }) ?? [],
      );
    }

    return (
      routinesToMap?.map((r) => ({
        ...r,
        activities: r.activities?.map((ra) => ({
          ...ra,
          activityName:
            activities?.find((act) => act.id === ra.activityID)?.name || "",
          activityType:
            activities?.find((act) => act.id === ra.activityID)?.type || "",
        })),
      })) ?? []
    );
  }

  useEffect(() => {
    if (viewType === "export" && serviceOrder.userExecutor)
      fetchExecutorCompany(serviceOrder.userExecutor);
    else setExecutorCompany({ data: null, loading: false });
  }, [viewType, serviceOrder]);

  return (
    <>
      <Grid gap="15px" id={`export-pdf-order-${serviceOrder.code}`}>
        {viewType === "export" && (
          <Flex justifyContent="space-between">
            <Flex gap="5px">
              {executorCompany.loading ? (
                <Loader />
              ) : (
                executorCompany.data && (
                  <>
                    <Label>{strings.executorCompany}:</Label>
                    <span className="text-muted">
                      {executorCompany.loading ? (
                        <Loader />
                      ) : (
                        executorCompany.data
                      )}
                    </span>
                  </>
                )
              )}
            </Flex>
            <Flex gap="5px">
              <Label>{strings.orderCode}:</Label>
              <span className="text-muted">{serviceOrder.code}</span>
            </Flex>
          </Flex>
        )}
        <Grid templateColumns={{ large: "1fr" }} gap="15px">
          <ServiceOrderInfo serviceOrder={serviceOrder} />
        </Grid>
        <Grid templateColumns={{ large: "1fr 1fr 1fr" }} gap="15px">
          <ExecutorCard
            executorID={serviceOrder.userExecutor}
            hasAuxExecutors
            auxExecutors={serviceOrder.auxExecutors}
            readOnly
            hidePhoto={viewType === "export"}
          />
          <ToolsInfo toolIDs={serviceOrder.tools} />
          <SparesInfo serviceOrderID={serviceOrder.id} />
        </Grid>
        <Grid templateColumns={{ large: "1fr" }} gap="15px">
          <div className="avoidBreak">
            <EquipmentsList serviceOrderID={serviceOrder.id} />
          </div>
          <div className="avoidBreak">
            <ServiceOrderLogs serviceOrderID={serviceOrder.id} simple />
          </div>
          {(serviceOrder.status === serviceOrderStatus.executed ||
            serviceOrder.status === serviceOrderStatus.executing) && (
            <ExecutedOrderCard serviceOrderID={serviceOrder.id} />
          )}
          <div className="avoidBreak">
            <MaintenanceRoutinesInfo
              equipmentRoutines={serviceOrder.equipmentRoutines}
              maintenanceRoutineIDs={serviceOrder.maintenanceRoutines}
            />
          </div>
          {viewType === "execute" ? (
            <TextAreaField
              name="observations"
              label={strings.observations}
              value={serviceOrder.observations}
              onChange={onChangeObservations}
            ></TextAreaField>
          ) : (
            <Grid>
              <Label>{strings.observations}</Label>
              <span className="text-muted">{serviceOrder.observations}</span>
            </Grid>
          )}
        </Grid>
        {!fromEquipment && (
          <Flex justifyContent="end" data-html2canvas-ignore>
            {viewType !== "export" && (
              <>
                {online && (
                  <>
                    {localData ? (
                      <Button
                        onClick={onLocalClean}
                        variation="link"
                        colorTheme="error"
                      >
                        {strings.localClean}
                      </Button>
                    ) : (
                      <Button onClick={onLocalSubmit} variation="link">
                        {strings.localSave}
                      </Button>
                    )}
                  </>
                )}
                <Button onClick={onSave}>
                  <Flex gap={2}>
                    <Save /> {strings.save}
                  </Flex>
                </Button>
                {serviceOrder.status === serviceOrderStatus.executing && (
                  <Button onClick={onPause}>
                    <Flex gap={2}>
                      <Pause /> {strings.pause}
                    </Flex>
                  </Button>
                )}
                {serviceOrder.status === serviceOrderStatus.paused && (
                  <Button onClick={onResume}>
                    <Flex gap={2}>
                      <PlayArrow /> {strings.resume}
                    </Flex>
                  </Button>
                )}
                <Button
                  disabled={
                    serviceOrder.status === serviceOrderStatus.executing &&
                    !online
                  }
                  onClick={onSubmit}
                  variation="primary"
                >
                  {serviceOrder.status !== serviceOrderStatus.executing &&
                  serviceOrder.status !== serviceOrderStatus.paused ? (
                    <Flex gap={2}>
                      <PlayArrow /> {strings.startExecution}
                    </Flex>
                  ) : (
                    <Flex gap={2}>
                      <Stop /> {strings.endExecution}
                    </Flex>
                  )}
                </Button>
              </>
            )}
            {viewType === "export" && (
              <Button onClick={onExport} variation="primary">
                {strings.exportOrder}
              </Button>
            )}
          </Flex>
        )}
      </Grid>
      {loading && <LoaderFullScreen />}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    observations: "Observaciones",
    startExecution: "Iniciar ejecución",
    endExecution: "Finalizar ejecución",
    exportOrder: "Exportar Orden",
    save: "Guardar",
    pause: "Pausar ejecución",
    resume: "Reanudar ejecución",
    localSave: "Activar modo offline",
    localClean: "Desactivar modo offline",
    successSavedLocal: "Se ha activado el modo offline con éxito",
    successCleanLocal: "Se ha desactivado el modo offline con éxito",
    successStarted: "Se ha iniciado la ejecución de la orden",
    successExecuted: "Se ha finalizado la ejecución de la orden",
    successExported: "Orden de Servicio exportada con éxito",
    successSaved: "Se han guardado los datos de ejecución de la orden",
    successPaused: "Se ha pausado la ejecución de la orden",
    successResumed: "Se ha reanudado la ejecución de la orden",
    responses: {
      unexpectedError: "Ha ocurrido un error inesperado",
      locationNotFound: "No se pudo verificar la ubicación del técnico",
      locationNotSupported: "Geolocalización no soportada",
      errorFetchEquipments: "Error al consultar los equipos",
      errorFetchPhotos: "Error al consultar las fotos de la orden",
    },
    missingActivities: "Faltan actividades por completar",
    invalidTechnician: "No puedes ejecutar la orden de otro técnico",
    soFarFromService:
      "No estás lo suficientemente cerca para la ejecución de la orden",
    executorCompany: "Empresa Ejecutora",
    orderCode: "Código de Orden",
    photosRequired: "Se necesita al menos una foto para finalizar la orden",
    orderCanceled: "La orden fue anulada",
  },
});

export default ExecutedServiceOrder;
